/**
 * Mask-aware blending of an AI-edit result over the original source.
 *
 * Used by the brush-based recolour flow to GUARANTEE that anything outside
 * the user's brush stroke is restored to the original photo, even if the
 * model bled colour outside the masked area or left residual mask pixels.
 *
 * Inputs are data URLs:
 *   - originalSourceDataUrl: the user's clean upload (original dimensions)
 *   - resultDataUrl: Gemini's output (already cropped back to original
 *     dimensions via cropResultToOriginal in App.tsx)
 *   - maskDataUrl: a binary or near-binary mask. Any non-zero R channel is
 *     treated as "inside the mask". The mask is resampled to the result's
 *     dimensions so callers don't need to align it.
 *
 * The mask is feathered by `featherPx` pixels via a separable box blur so
 * the seam between recoloured and preserved areas is invisible.
 */

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (!src.startsWith('data:')) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });

const drawAt = (img: HTMLImageElement, w: number, h: number): ImageData | null => {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
};

/**
 * Separable box blur on a single-channel Uint8 buffer (in-place).
 * Used to feather the binary mask so the composite seam is invisible.
 */
const boxBlurChannel = (
  buf: Uint8ClampedArray,
  w: number,
  h: number,
  radius: number
): void => {
  if (radius < 1) return;
  const tmp = new Uint8ClampedArray(buf.length);
  const r = Math.max(1, Math.floor(radius));
  const denom = r * 2 + 1;

  // Horizontal pass: buf -> tmp
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let sum = 0;
    for (let x = -r; x <= r; x++) {
      sum += buf[row + Math.max(0, Math.min(w - 1, x))];
    }
    for (let x = 0; x < w; x++) {
      tmp[row + x] = sum / denom;
      const nx = x + r + 1;
      const ox = x - r;
      sum += buf[row + Math.max(0, Math.min(w - 1, nx))]
           - buf[row + Math.max(0, Math.min(w - 1, ox))];
    }
  }

  // Vertical pass: tmp -> buf
  for (let x = 0; x < w; x++) {
    let sum = 0;
    for (let y = -r; y <= r; y++) {
      sum += tmp[Math.max(0, Math.min(h - 1, y)) * w + x];
    }
    for (let y = 0; y < h; y++) {
      buf[y * w + x] = sum / denom;
      const ny = y + r + 1;
      const oy = y - r;
      sum += tmp[Math.max(0, Math.min(h - 1, ny)) * w + x]
           - tmp[Math.max(0, Math.min(h - 1, oy)) * w + x];
    }
  }
};

export interface BlendOptions {
  /** Feather radius in pixels at the result's resolution. Default: 0.5% of the smaller dimension, min 6, max 48. */
  featherPx?: number;
}

/**
 * Returns a PNG data URL of `result` composited over `originalSource` using
 * `mask` (white = take from result, black = take from original). The mask is
 * feathered before compositing for a seamless blend.
 */
export const compositeMaskedResult = async (
  originalSourceDataUrl: string,
  resultDataUrl: string,
  maskDataUrl: string,
  options: BlendOptions = {}
): Promise<string> => {
  const [origImg, resultImg, maskImg] = await Promise.all([
    loadImage(originalSourceDataUrl),
    loadImage(resultDataUrl),
    loadImage(maskDataUrl),
  ]);

  const w = resultImg.width;
  const h = resultImg.height;
  if (w === 0 || h === 0) return resultDataUrl;

  const origData = drawAt(origImg, w, h);
  const resData = drawAt(resultImg, w, h);
  const maskData = drawAt(maskImg, w, h);
  if (!origData || !resData || !maskData) return resultDataUrl;

  // Build a single-channel mask buffer from the mask image's R channel.
  // Anything non-zero on R is treated as "inside the mask".
  const mPx = maskData.data;
  const mBuf = new Uint8ClampedArray(w * h);
  for (let i = 0, j = 0; i < mPx.length; i += 4, j++) {
    mBuf[j] = mPx[i] > 8 ? 255 : 0;
  }

  const feather = Math.max(
    6,
    Math.min(48, Math.round(options.featherPx ?? Math.min(w, h) * 0.005))
  );
  boxBlurChannel(mBuf, w, h, feather);

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) return resultDataUrl;

  const outImage = ctx.createImageData(w, h);
  const oPx = origData.data;
  const rPx = resData.data;
  const oOut = outImage.data;
  for (let i = 0, j = 0; i < oOut.length; i += 4, j++) {
    const a = mBuf[j] / 255; // 0 = original, 1 = result
    const ia = 1 - a;
    oOut[i]     = rPx[i]     * a + oPx[i]     * ia;
    oOut[i + 1] = rPx[i + 1] * a + oPx[i + 1] * ia;
    oOut[i + 2] = rPx[i + 2] * a + oPx[i + 2] * ia;
    oOut[i + 3] = 255;
  }
  ctx.putImageData(outImage, 0, 0);

  return out.toDataURL('image/png');
};

/**
 * Pixel-lock the CEILING / UPPER-WALL BAND of a staged result back to the
 * original photo.
 *
 * Why: `gemini-3-pro-image-preview` re-renders the whole scene, so even with
 * strict prompts it can subtly shift the top of the walls, the ceiling line,
 * and the perspective/vanishing points. The very top band of an interior
 * photo is almost always fixed architecture (ceiling, cornices, tops of
 * windows/doors) that staging should NEVER touch. Compositing the original's
 * top band back over the result GUARANTEES that region — and the perspective
 * lines it anchors — stays pixel-identical to the source.
 *
 * `fraction` is the share of image HEIGHT locked from the top (e.g. 0.08 =
 * top 8%). The lower edge of the band is feathered so the seam is invisible.
 *
 * CAVEAT: anything the model legitimately adds into this band (e.g. the top
 * of tall curtains, a very tall plant, or ceiling-height artwork) will be
 * clipped back to the original. Keep `fraction` conservative for this reason,
 * and set it to 0 to disable entirely. Not suitable for 360 equirectangular
 * panoramas (the top band is the polar cap) — callers must skip those.
 */
export const compositeTopBand = async (
  originalSourceDataUrl: string,
  resultDataUrl: string,
  fraction: number,
  featherPx?: number
): Promise<string> => {
  if (!(fraction > 0)) return resultDataUrl;

  const [origImg, resultImg] = await Promise.all([
    loadImage(originalSourceDataUrl),
    loadImage(resultDataUrl),
  ]);

  const w = resultImg.width;
  const h = resultImg.height;
  if (w === 0 || h === 0) return resultDataUrl;

  const origData = drawAt(origImg, w, h);
  const resData = drawAt(resultImg, w, h);
  if (!origData || !resData) return resultDataUrl;

  // Build a top-band mask (255 = take from original, 0 = take from result),
  // then feather its lower edge for a seamless transition.
  const bandH = Math.max(1, Math.min(h, Math.round(h * fraction)));
  const mBuf = new Uint8ClampedArray(w * h);
  for (let y = 0; y < bandH; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) mBuf[row + x] = 255;
  }
  const feather = Math.max(6, Math.min(64, Math.round(featherPx ?? h * 0.02)));
  boxBlurChannel(mBuf, w, h, feather);

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) return resultDataUrl;

  const outImage = ctx.createImageData(w, h);
  const oPx = origData.data;
  const rPx = resData.data;
  const oOut = outImage.data;
  for (let i = 0, j = 0; i < oOut.length; i += 4, j++) {
    const a = mBuf[j] / 255; // 1 = original (locked), 0 = result
    const ia = 1 - a;
    oOut[i]     = oPx[i]     * a + rPx[i]     * ia;
    oOut[i + 1] = oPx[i + 1] * a + rPx[i + 1] * ia;
    oOut[i + 2] = oPx[i + 2] * a + rPx[i + 2] * ia;
    oOut[i + 3] = 255;
  }
  ctx.putImageData(outImage, 0, 0);

  return out.toDataURL('image/png');
};
